/**
 * Gössl Theme - Global JavaScript
 * Utilities and shared functionality
 */

(function() {
  'use strict';

  /**
   * Debounce function for performance
   * @param {Function} fn - Function to debounce
   * @param {number} wait - Wait time in ms
   * @returns {Function}
   */
  function debounce(fn, wait) {
    let timer;
    return function(...args) {
      clearTimeout(timer);
      timer = setTimeout(() => fn.apply(this, args), wait);
    };
  }

  /**
   * Throttle function for performance
   * @param {Function} fn - Function to throttle
   * @param {number} limit - Time limit in ms
   * @returns {Function}
   */
  function throttle(fn, limit) {
    let inThrottle;
    return function(...args) {
      if (!inThrottle) {
        fn.apply(this, args);
        inThrottle = true;
        setTimeout(() => inThrottle = false, limit);
      }
    };
  }

  // Chrome keeps layout boxes for the content of a closed <details>, so getClientRects() cannot tell that
  // Tab skips it: only the summary of a closed <details> is reachable
  function inClosedDetails(el) {
    for (let d = el.parentElement?.closest('details'); d; d = d.parentElement?.closest('details')) {
      if (!d.open && !(el.tagName === 'SUMMARY' && el.parentElement === d)) return true;
    }
    return false;
  }

  /**
   * The Tab stops inside an element, read fresh on every call: overlays redraw while they are open
   * (cart drawer, quick view, store list), so a list taken at open time goes stale. Leaves out what
   * Tab cannot reach: disabled, tabindex -1, hidden inputs, anything not rendered (display:none, the
   * body of a closed <details>), visibility:hidden and inert subtrees (a11y #902).
   * @param {HTMLElement} root
   * @returns {HTMLElement[]}
   */
  function focusableIn(root) {
    const candidates = root.querySelectorAll(
      'a[href], area[href], button, input:not([type="hidden"]), select, textarea, summary, iframe, [tabindex], [contenteditable="true"]'
    );
    return Array.prototype.filter.call(candidates, (el) =>
      !el.matches(':disabled, [tabindex="-1"]') &&
      el.getClientRects().length > 0 &&
      getComputedStyle(el).visibility !== 'hidden' &&
      !el.closest('[inert]') &&
      !inClosedDetails(el)
    );
  }

  /**
   * Trap focus within an element (for modals/dialogs)
   * @param {HTMLElement} element - Element to trap focus within
   * @param {HTMLElement} [initial] - Element to focus now (default: the first Tab stop)
   * @returns {Function} releases the trap
   */
  function trapFocus(element, initial) {
    function handleTab(e) {
      if (e.key !== 'Tab') return;

      const focusable = focusableIn(element);
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (!first) return;

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }

    element.addEventListener('keydown', handleTab);
    (initial || focusableIn(element)[0])?.focus({ preventScroll: true });

    return () => element.removeEventListener('keydown', handleTab);
  }

  // Overlays opened through overlayFocus, the last one on top. Only the top one answers keys.
  const openOverlays = [];

  function onOverlayKeydown(e) {
    const top = openOverlays[openOverlays.length - 1];
    if (!top) return;
    if (e.key === 'Escape') {
      top.onEscape?.();
    } else if (e.key === 'Tab' && !top.container.contains(document.activeElement)) {
      // focus sits outside the overlay (its element was redrawn away): the Tab lands inside again
      const focusable = focusableIn(top.container);
      if (!focusable.length) return;
      e.preventDefault();
      focusable[e.shiftKey ? focusable.length - 1 : 0].focus();
    }
  }

  function canTakeFocus(el) {
    return !!el && el !== document.body && el.isConnected && typeof el.focus === 'function' &&
      el.getClientRects().length > 0 && !el.matches(':disabled') && !el.closest('[inert]');
  }

  /**
   * One open/close behaviour for the overlays (a11y #902): open() remembers what had focus, traps Tab
   * inside the container and focuses its first Tab stop (or opts.initialFocus()); Escape calls
   * opts.onEscape; close() gives focus back to the opener, or to opts.fallback() when the opener is
   * gone. A popup opened over a drawer answers Escape alone.
   * @param {HTMLElement} container
   * @param {{onEscape?: Function, initialFocus?: Function, fallback?: Function}} [opts]
   * @returns {{open: Function, close: Function}} open(opener?) - the opener defaults to document.activeElement
   */
  function overlayFocus(container, opts = {}) {
    const entry = { container, onEscape: opts.onEscape };
    let release = null;
    let opener = null;

    function open(openerEl) {
      if (release) return;
      opener = openerEl || document.activeElement;
      release = trapFocus(container, opts.initialFocus?.());
      openOverlays.push(entry);
      if (openOverlays.length === 1) document.addEventListener('keydown', onOverlayKeydown);
    }

    function close() {
      if (!release) return;
      release();
      release = null;
      openOverlays.splice(openOverlays.indexOf(entry), 1);
      if (!openOverlays.length) document.removeEventListener('keydown', onOverlayKeydown);
      const target = canTakeFocus(opener) ? opener : opts.fallback?.();
      opener = null;
      // Focus goes back only if it is still ours to give - inside the overlay, or lost to the page
      const active = document.activeElement;
      if (active && active !== document.body && !container.contains(active)) return;
      if (canTakeFocus(target)) {
        target.focus();
      } else {
        // nothing to go back to (a timer opened it): the next Tab starts at the top of the page, not behind the
        // overlay where the hidden field kept the place
        document.body.setAttribute('tabindex', '-1');
        document.body.focus({ preventScroll: true });
        document.body.removeAttribute('tabindex');
      }
    }

    return { open, close };
  }

  /**
   * Remove focus trap
   * @param {HTMLElement} element - Element to remove trap from
   */
  function removeTrapFocus(element) {
    element?.blur();
  }

  /**
   * Fetch cart data
   * @returns {Promise<Object>}
   */
  async function fetchCart() {
    try {
      const response = await fetch(window.g26url('cart.js'), {
        headers: { 'Content-Type': 'application/json' }
      });
      return await response.json();
    } catch (error) {
      console.error('Error fetching cart:', error);
      return null;
    }
  }

  /**
   * Add item to cart
   * @param {number} variantId - Variant ID
   * @param {number} quantity - Quantity to add
   * @returns {Promise<Object>}
   */
  async function addToCart(variantId, quantity = 1) {
    try {
      const response = await fetch(window.g26url('cart/add.js'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: variantId,
          quantity: quantity
        })
      });

      if (!response.ok) {
        throw new Error('Add to cart failed');
      }

      const data = await response.json();
      dispatchCartUpdate();
      return data;
    } catch (error) {
      console.error('Error adding to cart:', error);
      throw error;
    }
  }

  /**
   * Update cart item quantity
   * @param {string} key - Line item key
   * @param {number} quantity - New quantity
   * @returns {Promise<Object>}
   */
  async function updateCartItem(key, quantity) {
    try {
      const response = await fetch(window.g26url('cart/change.js'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: key,
          quantity: quantity
        })
      });

      if (!response.ok) {
        throw new Error('Update cart failed');
      }

      const data = await response.json();
      dispatchCartUpdate();
      return data;
    } catch (error) {
      console.error('Error updating cart:', error);
      throw error;
    }
  }

  /**
   * Dispatch cart update event
   */
  function dispatchCartUpdate() {
    document.dispatchEvent(new CustomEvent('cart:updated'));
  }

  /**
   * Update cart count in header
   * @param {number} count - Item count
   */
  function updateCartCount(count) {
    const cartCountElements = document.querySelectorAll('[data-cart-count]');
    cartCountElements.forEach(el => {
      el.textContent = count;
      el.setAttribute('aria-label', window.g26tText('cart.general.live_region', '{{ count }} Artikel im Warenkorb', { count: count }));
      // r71: the badge renders with .is-empty (display:none) on an empty cart —
      // without toggling it the count never appeared after an AJAX add
      el.classList.toggle('is-empty', !count);
    });

    // Update live region for screen readers
    const liveRegion = document.getElementById('cart-live-region');
    if (liveRegion) {
      liveRegion.textContent = window.g26tText('cart.general.live_region', '{{ count }} Artikel im Warenkorb', { count: count });
    }
  }

  /**
   * Format money according to Shopify format
   * @param {number} cents - Amount in cents
   * @param {string} format - Money format string
   * @returns {string}
   */
  function formatMoney(cents, format = '{{amount_with_comma_separator}} €') {
    if (typeof cents === 'string') {
      cents = cents.replace('.', '');
    }

    const value = cents / 100;
    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;

    function formatWithDelimiters(number, precision = 2, thousands = '.', decimal = ',') {
      if (isNaN(number) || number == null) return '0';

      number = (number / 100).toFixed(precision);
      const parts = number.split('.');
      const dollars = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1' + thousands);
      const cents = parts[1] ? decimal + parts[1] : '';

      return dollars + cents;
    }

    switch (format.match(placeholderRegex)?.[1]) {
      case 'amount':
        return format.replace(placeholderRegex, formatWithDelimiters(cents, 2, ',', '.'));
      case 'amount_no_decimals':
        return format.replace(placeholderRegex, formatWithDelimiters(cents, 0, ',', '.'));
      case 'amount_with_comma_separator':
        return format.replace(placeholderRegex, formatWithDelimiters(cents, 2, '.', ','));
      case 'amount_no_decimals_with_comma_separator':
        return format.replace(placeholderRegex, formatWithDelimiters(cents, 0, '.', ','));
      default:
        return format.replace(placeholderRegex, value.toFixed(2));
    }
  }

  /**
   * Get URL parameters
   * @returns {URLSearchParams}
   */
  function getUrlParams() {
    return new URLSearchParams(window.location.search);
  }

  /**
   * Update URL parameter without page reload
   * @param {string} key - Parameter key
   * @param {string} value - Parameter value
   */
  function updateUrlParam(key, value) {
    const url = new URL(window.location);
    if (value) {
      url.searchParams.set(key, value);
    } else {
      url.searchParams.delete(key);
    }
    window.history.replaceState({}, '', url);
  }

  /**
   * Lazy load images with IntersectionObserver
   */
  function initLazyLoading() {
    if ('loading' in HTMLImageElement.prototype) {
      // Browser supports native lazy loading
      return;
    }

    // Fallback for older browsers
    const lazyImages = document.querySelectorAll('img[loading="lazy"]');

    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver((entries, observer) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const img = entry.target;
            img.src = img.dataset.src || img.src;
            img.srcset = img.dataset.srcset || img.srcset || '';
            observer.unobserve(img);
          }
        });
      }, {
        rootMargin: '50px'
      });

      lazyImages.forEach(img => observer.observe(img));
    }
  }

  /**
   * Smooth scroll to element
   * @param {string|HTMLElement} target - Target element or selector
   * @param {number} offset - Offset from top
   */
  function scrollToElement(target, offset = 0) {
    const element = typeof target === 'string' ? document.querySelector(target) : target;
    if (!element) return;

    const headerHeight = document.querySelector('.header')?.offsetHeight || 0;
    const top = element.getBoundingClientRect().top + window.scrollY - headerHeight - offset;

    window.scrollTo({
      top: top,
      behavior: 'smooth'
    });
  }

  /**
   * Initialize accordion functionality
   */
  function initAccordions() {
    const accordions = document.querySelectorAll('[data-accordion]');

    accordions.forEach(accordion => {
      const trigger = accordion.querySelector('[data-accordion-trigger]');
      const content = accordion.querySelector('[data-accordion-content]');

      if (!trigger || !content) return;

      trigger.addEventListener('click', () => {
        const isOpen = trigger.getAttribute('aria-expanded') === 'true';

        trigger.setAttribute('aria-expanded', !isOpen);
        content.hidden = isOpen;

        // Animate height
        if (!isOpen) {
          content.style.height = '0';
          content.hidden = false;
          content.style.height = content.scrollHeight + 'px';
        } else {
          content.style.height = content.scrollHeight + 'px';
          requestAnimationFrame(() => {
            content.style.height = '0';
          });
        }
      });

      content.addEventListener('transitionend', () => {
        if (trigger.getAttribute('aria-expanded') === 'true') {
          content.style.height = 'auto';
        }
      });
    });
  }

  /**
   * Handle reduced motion preference
   */
  function handleReducedMotion() {
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    document.documentElement.classList.toggle('reduce-motion', prefersReducedMotion);
  }

  /**
   * Initialize on DOM ready
   */
  function init() {
    initLazyLoading();
    initAccordions();
    handleReducedMotion();

    // Listen for reduced motion changes
    window.matchMedia('(prefers-reduced-motion: reduce)').addEventListener('change', handleReducedMotion);

    // Cart update listener
    document.addEventListener('cart:updated', async () => {
      const cart = await fetchCart();
      if (cart) {
        updateCartCount(cart.item_count);
      }
    });
  }

  // Export utilities to global scope (merge — don't clobber any keys
  // that other modules like configurator/wishlist/outfits may have already set
  // when load order doesn't put global.js first).
  window.Goessl = window.Goessl || {};
  Object.assign(window.Goessl, {
    debounce,
    throttle,
    trapFocus,
    removeTrapFocus,
    overlayFocus,
    fetchCart,
    addToCart,
    updateCartItem,
    updateCartCount,
    formatMoney,
    getUrlParams,
    updateUrlParam,
    scrollToElement,
    dispatchCartUpdate
  });

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Lazy image fade-in: mark images as loaded when they finish loading
  document.addEventListener('load', function(e) {
    if (e.target.tagName === 'IMG' && e.target.classList.contains('goessl-product-card__img')) {
      e.target.classList.add('is-loaded');
    }
  }, true);
})();
